import { Link as LinkIcon, Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { useChatbot } from '../../contexts/ChatbotContext';
import { useDesignColors } from '../../hooks/useDesignColors';
import { useRealEstateSystem } from '../../hooks/useRealEstateSystem';
import { supabase } from '../../lib/supabase';
import { RealEstate } from '../../types/RealEstate';

interface RealEstateFormProps {
  property?: RealEstate;
  onClose: () => void;
}

export function RealEstateForm({ property, onClose }: RealEstateFormProps) {
  const { chatbotPublicId } = useChatbot();
  const { addProperty, updateProperty, categories, tags } = useRealEstateSystem();
  const { colors } = useDesignColors();
  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    title: property?.title || '',
    description: property?.description || '',
    price: property?.price || '',
    currency: property?.currency || 'EUR',
    surface: property?.surface || '',
    rooms: property?.rooms || '',
    bedrooms: property?.bedrooms || '',
    bathrooms: property?.bathrooms || '',
    location: property?.location || '',
    address: property?.address || '',
    postal_code: property?.postal_code || '',
    city: property?.city || '',
    country: property?.country || '',
    type: property?.type || '',
    status: property?.status || 'available',
    transaction_type: property?.transaction_type || 'sale',
    images: property?.images || [],
    category: property?.category || '',
    subcategory: property?.subcategory || '',
    tags: property?.tags || [],
    features: property?.features || [],
    target_roi: property?.target_roi || '',
    estimated_resale: property?.estimated_resale || '',
    external_url: property?.metadata?.external_url || ''
  });

  // Récupérer les sous-catégories disponibles
  const subcategories = categories
    .filter(cat => cat.parent_id && cat.parent_id === categories.find(c => c.name === formData.category)?.id)
    .map(cat => cat.name);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatbotPublicId) return;

    const propertyData = {
      title: formData.title,
      description: formData.description,
      price: formData.price ? parseFloat(formData.price.toString()) : undefined,
      currency: formData.currency,
      surface: formData.surface ? parseFloat(formData.surface.toString()) : undefined,
      rooms: formData.rooms ? parseInt(formData.rooms.toString()) : undefined,
      bedrooms: formData.bedrooms ? parseInt(formData.bedrooms.toString()) : undefined,
      bathrooms: formData.bathrooms ? parseInt(formData.bathrooms.toString()) : undefined,
      location: formData.location,
      address: formData.address,
      postal_code: formData.postal_code,
      city: formData.city,
      country: formData.country,
      type: formData.type,
      status: formData.status,
      transaction_type: formData.transaction_type,
      images: formData.images,
      category: formData.category,
      subcategory: formData.subcategory,
      tags: formData.tags,
      features: formData.features,
      target_roi: formData.target_roi ? parseFloat(formData.target_roi.toString()) : undefined,
      estimated_resale: formData.estimated_resale,
      metadata: {
        external_url: formData.external_url
      }
    };

    try {
      if (property) {
        await updateProperty(chatbotPublicId, property.id, propertyData);
      } else {
        await addProperty(chatbotPublicId, propertyData);
      }
      onClose();
    } catch (err) {
      console.error('Error saving property:', err);
      setError(err instanceof Error ? err.message : 'An error has occurred');
    }
  };

  const handleImageUpload = async (file: File) => {
    if (!chatbotPublicId) return;
    try {
      setUploadingImages(true);

      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `realestate/${chatbotPublicId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('chatbot-assets')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('chatbot-assets')
        .getPublicUrl(filePath);

      setFormData(prev => ({
        ...prev,
        images: [...prev.images, publicUrl]
      }));
    } catch (err) {
      console.error('Error uploading image:', err);
    } finally {
      setUploadingImages(false);
    }
  };

  const handleRemoveImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleTagToggle = (tagName: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tagName)
        ? prev.tags.filter(t => t !== tagName)
        : [...prev.tags, tagName]
    }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-4xl h-[90vh] flex flex-col bg-gray-900/95 rounded-xl border border-white/10">
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h3 className="text-lg font-semibold text-white">
            {property ? 'Modify the property' : 'Add a property'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>

        <form id="propertyForm" onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-2 gap-6">
            {/* Colonne de gauche */}
            <div className="space-y-6">
              {/* Informations de base */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-300">Background information</h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Type de property
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  >
                    <option value="">Select a type</option>
                    <option value="apartment">Apartment</option>
                    <option value="house">House</option>
                    <option value="land">Ground</option>
                    <option value="commercial">Commercial premises</option>
                    <option value="office">Office</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    rows={4}
                  />
                </div>
              </div>

              {/* Catégories et Tags */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-300">Categorisation</h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Category
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      category: e.target.value,
                      subcategory: '' // Reset subcategory when category changes
                    }))}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Local Select a category</option>
                    {categories
                      .filter(cat => !cat.parent_id) // Only show parent categories
                      .map(category => (
                        <option key={category.id} value={category.name}>
                          {category.name}
                        </option>
                      ))
                    }
                  </select>
                </div>

                {formData.category && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Subcategory
                    </label>
                    <select
                      value={formData.subcategory}
                      onChange={(e) => setFormData(prev => ({ ...prev, subcategory: e.target.value }))}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Select a subcategory</option>
                      {subcategories.map(subcategory => (
                        <option key={subcategory} value={subcategory}>
                          {subcategory}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Tags
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {tags.map(tag => (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => handleTagToggle(tag.name)}
                        className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                          formData.tags.includes(tag.name)
                            ? 'bg-indigo-500/20 text-indigo-400'
                            : 'bg-white/5 text-gray-300 hover:bg-white/10'
                        }`}
                      >
                        {tag.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Prix et transaction */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-300">Price and transaction</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Price
                    </label>
                    <input
                      type="number"
                      value={formData.price}
                      onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Currency
                    </label>
                    <select
                      value={formData.currency}
                      onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="EUR">EUR</option>
                      <option value="USD">USD</option>
                      <option value="GBP">GBP</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Type of transaction
                  </label>
                  <select
                    value={formData.transaction_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, transaction_type: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="sale">Sale</option>
                    <option value="rental">Rental</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Colonne de droite */}
            <div className="space-y-6">
              {/* Caractéristiques */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-300">Features</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Surface (m²)
                    </label>
                    <input
                      type="number"
                      value={formData.surface}
                      onChange={(e) => setFormData(prev => ({ ...prev, surface: e.target.value }))}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Rooms
                    </label>
                    <input
                      type="number"
                      value={formData.rooms}
                      onChange={(e) => setFormData(prev => ({ ...prev, rooms: e.target.value }))}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Bedrooms
                    </label>
                    <input
                      type="number"
                      value={formData.bedrooms}
                      onChange={(e) => setFormData(prev => ({ ...prev, bedrooms: e.target.value }))}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Bathrooms
                    </label>
                    <input
                      type="number"
                      value={formData.bathrooms}
                      onChange={(e) => setFormData(prev => ({ ...prev, bathrooms: e.target.value }))}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>

              {/* Localisation */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-300">Location</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Address
                    </label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Postal code
                    </label>
                    <input
                      type="text"
                      value={formData.postal_code}
                      onChange={(e) => setFormData(prev => ({ ...prev, postal_code: e.target.value }))}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      City
                    </label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Country
                    </label>
                    <input
                      type="text"
                      value={formData.country}
                      onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>

              {/* Rentabilité et revente */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-300">Profitability and resale</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      ROI (%)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.target_roi}
                      onChange={(e) => setFormData(prev => ({ ...prev, target_roi: e.target.value }))}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Ex: 12.5"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Estimated resale
                    </label>
                    <input
                      type="text"
                      value={formData.estimated_resale}
                      onChange={(e) => setFormData(prev => ({ ...prev, estimated_resale: e.target.value }))}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Ex: 6 mois, 2 ans, 350000€..."
                    />
                  </div>
                </div>
              </div>

              {/* Images */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-300">Images</h4>
                
                <div className="grid grid-cols-3 gap-4">
                  {formData.images.map((image, index) => (
                    <div key={index} className="relative aspect-square">
                      <img
                        src={image}
                        alt={`Image ${index + 1}`}
                        className="w-full h-full object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(index)}
                        className="absolute top-2 right-2 p-1 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <label className="aspect-square relative border-2 border-dashed border-white/10 rounded-lg hover:border-indigo-500/50 transition-colors cursor-pointer">
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleImageUpload(file);
                      }}
                      className="hidden"
                      disabled={uploadingImages}
                    />
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <Plus className="w-8 h-8 text-gray-400" />
                      <span className="text-sm text-gray-400 mt-2">Add</span>
                    </div>
                    {uploadingImages && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500" />
                      </div>
                    )}
                  </label>
                </div>
              </div>

              {/* URL externe */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  URL of the full record
                </label>
                <div className="relative">
                  <input
                    type="url"
                    value={formData.external_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, external_url: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="https://..."
                  />
                  <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                </div>
              </div>
            </div>
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-white/10">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-white/5 text-gray-300 hover:bg-white/10 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="propertyForm"
            disabled={loading}
            className="px-4 py-2 rounded-lg bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                {property ? 'Update' : 'Add'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}